import React from 'react';
import { type Rect } from '../types';
import type Canvas from './Canvas';
import { createRoot } from 'react-dom/client';

interface TooltipProps extends Rect {
  children: React.ReactNode;
  down?: boolean;
}

const WhiteboardTooltip: React.FC<TooltipProps> = ({
  left,
  top,
  width = 1,
  height = 1,
  children,
  down = false,
}) => {
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width,
        height,
        pointerEvents: 'none',
      }}
    >
      {/* <aha-tooltip> requires a "trigger" element before it, but we are manually opening it, so we just add an empty element */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
        }}
      />
      <aha-tooltip open placement={down ? 'bottom' : null}>
        {children}
      </aha-tooltip>
    </div>
  );
};

/**
 * Renders an `<aha-toolip>` at the specified coordinates. Useful for showing a tooltip on top of Whiteboard content.
 *
 * Returns a function that can be called to hide/unmount the tooltip. If a `canvas` object is provided, it will
 * automatically close the tooltip on pan or zoom.
 */
export const renderTooltip = (
  content: React.ReactNode,
  props: Omit<TooltipProps, 'children'>,
  canvas: Canvas
) => {
  const container = document.createElement('div');
  const root = createRoot(container);
  canvas.portalElement.appendChild(container);
  root.render(<WhiteboardTooltip {...props}>{content}</WhiteboardTooltip>);

  const closeTooltip = () => {
    if (container) {
      root.unmount();
      container.remove();
    }
  };

  canvas.on('pan', closeTooltip);
  canvas.on('zoom', closeTooltip);

  return closeTooltip;
};
